import { useEffect, useState } from 'react';
import { api } from '@/api';
import type { TimesheetWithDetails, TimesheetWeekView } from '@ultisim/shared';
import { format } from 'date-fns';

type Tab = 'pending' | 'all';

export default function AdminTimesheetsPage() {
  const [activeTab, setActiveTab] = useState<Tab>('pending');
  const [pending, setPending] = useState<TimesheetWithDetails[]>([]);
  const [allTimesheets, setAllTimesheets] = useState<TimesheetWithDetails[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [expandedView, setExpandedView] = useState<TimesheetWeekView | null>(null);
  const [rejectNotes, setRejectNotes] = useState('');
  const [approveNotes, setApproveNotes] = useState('');
  const [processing, setProcessing] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    loadData();
  }, [activeTab, statusFilter]);

  useEffect(() => {
    if (successMsg) {
      const timer = setTimeout(() => setSuccessMsg(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMsg]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      if (activeTab === 'pending') {
        const data = await api.get<TimesheetWithDetails[]>('/timesheets/admin/pending');
        setPending(data);
      } else {
        const params: Record<string, string> = {};
        if (statusFilter) params.status = statusFilter;
        const data = await api.get<TimesheetWithDetails[]>('/timesheets/admin/all', params);
        setAllTimesheets(data);
      }
    } catch (err) {
      console.error('Failed to load timesheets:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleExpand = async (id: string) => {
    if (expandedId === id) {
      setExpandedId(null);
      setExpandedView(null);
      return;
    }
    setExpandedId(id);
    try {
      const data = await api.get<TimesheetWeekView>(`/timesheets/admin/${id}`);
      setExpandedView(data);
    } catch (err) {
      console.error('Failed to load timesheet detail:', err);
    }
  };

  const approveTimesheet = async (id: string) => {
    setProcessing(id);
    setError('');
    try {
      await api.post(`/timesheets/${id}/approve`, { notes: approveNotes || undefined });
      setSuccessMsg('Timesheet approved');
      setExpandedId(null);
      setExpandedView(null);
      setApproveNotes('');
      await loadData();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to approve');
    } finally {
      setProcessing(null);
    }
  };

  const rejectTimesheet = async (id: string) => {
    if (!rejectNotes.trim()) {
      setError('Notes are required when rejecting');
      return;
    }
    setProcessing(id);
    setError('');
    try {
      await api.post(`/timesheets/${id}/reject`, { notes: rejectNotes });
      setSuccessMsg('Timesheet rejected');
      setExpandedId(null);
      setExpandedView(null);
      setRejectNotes('');
      await loadData();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to reject');
    } finally {
      setProcessing(null);
    }
  };

  const statusBadge = (status: string) => {
    const colors: Record<string, string> = {
      draft: 'badge-gray',
      submitted: 'badge-primary',
      approved: 'badge-success',
      rejected: 'badge-danger',
    };
    return <span className={`badge ${colors[status] || 'badge-gray'}`}>{status}</span>;
  };

  const displayList = activeTab === 'pending' ? pending : allTimesheets;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-white">Timesheets</h1>
        <p className="text-dark-400 mt-1">Review and approve team timesheets</p>
      </div>

      {successMsg && (
        <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg text-green-400 text-sm">
          {successMsg}
        </div>
      )}
      {error && (
        <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center justify-between border-b border-dark-700">
        <div className="flex gap-6">
          <button
            onClick={() => setActiveTab('pending')}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'pending'
                ? 'border-primary-500 text-primary-400'
                : 'border-transparent text-dark-400 hover:text-dark-200'
            }`}
          >
            Pending Review ({pending.length})
          </button>
          <button
            onClick={() => setActiveTab('all')}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'all'
                ? 'border-primary-500 text-primary-400'
                : 'border-transparent text-dark-400 hover:text-dark-200'
            }`}
          >
            All Timesheets
          </button>
        </div>
        {activeTab === 'all' && (
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input text-sm py-1 px-3 w-auto mb-3"
          >
            <option value="">All statuses</option>
            <option value="draft">Draft</option>
            <option value="submitted">Submitted</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        )}
      </div>

      {/* Timesheet list */}
      {displayList.length === 0 ? (
        <div className="card text-center py-12 text-dark-400">
          {activeTab === 'pending' ? 'No timesheets awaiting review' : 'No timesheets found'}
        </div>
      ) : (
        <div className="space-y-4">
          {displayList.map((ts) => (
            <div key={ts.id} className="card">
              {/* Summary row */}
              <div
                className="flex items-center justify-between cursor-pointer"
                onClick={() => toggleExpand(ts.id)}
              >
                <div className="flex items-center gap-4">
                  <div>
                    <p className="font-medium text-white">
                      {ts.user.firstName} {ts.user.lastName}
                    </p>
                    <p className="text-sm text-dark-400">{ts.user.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-right">
                    <p className="text-sm text-dark-400">
                      {format(new Date(ts.weekStart), 'MMM d')} - {format(new Date(ts.weekEnd), 'MMM d, yyyy')}
                    </p>
                    <p className="text-lg font-bold text-white">{ts.totalHours}h</p>
                  </div>
                  {statusBadge(ts.status)}
                  <svg
                    className={`w-5 h-5 text-dark-400 transition-transform ${expandedId === ts.id ? 'rotate-180' : ''}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>

              {/* Expanded detail */}
              {expandedId === ts.id && expandedView && (
                <div className="mt-4 pt-4 border-t border-dark-700">
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[700px] text-sm">
                      <thead>
                        <tr>
                          <th className="text-left px-3 py-2 text-xs text-dark-400 uppercase">Project / Task</th>
                          {expandedView.dates.map((date) => (
                            <th key={date} className="px-2 py-2 text-center text-xs text-dark-400">
                              {format(new Date(date), 'EEE d')}
                            </th>
                          ))}
                          <th className="px-3 py-2 text-center text-xs text-dark-400">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {expandedView.entryGroups.map((group) => (
                          <tbody key={group.project.id}>
                            <tr className="bg-dark-800/30">
                              <td className="px-3 py-1.5" colSpan={expandedView.dates.length + 2}>
                                <div className="flex items-center gap-2">
                                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: group.project.color }} />
                                  <span className="font-medium text-dark-200">{group.project.name}</span>
                                  <span className="text-dark-500 ml-auto">{group.projectTotal}h</span>
                                </div>
                              </td>
                            </tr>
                            {group.tasks.map((task) => (
                              <tr key={task.task.id} className="border-t border-dark-800/30">
                                <td className="px-3 py-1.5 pl-8 text-dark-300">{task.task.title}</td>
                                {expandedView.dates.map((date) => (
                                  <td key={date} className="px-2 py-1.5 text-center text-dark-200">
                                    {task.dailyHours[date] || <span className="text-dark-600">-</span>}
                                  </td>
                                ))}
                                <td className="px-3 py-1.5 text-center font-medium text-white">{task.weekTotal}h</td>
                              </tr>
                            ))}
                          </tbody>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Approve/Reject actions (only for submitted) */}
                  {ts.status === 'submitted' && (
                    <div className="mt-4 pt-4 border-t border-dark-700 space-y-4">
                      <div>
                        <label className="block text-sm text-dark-400 mb-1">Notes (optional for approval, required for rejection)</label>
                        <textarea
                          value={expandedId === ts.id ? (rejectNotes || approveNotes) : ''}
                          onChange={(e) => { setRejectNotes(e.target.value); setApproveNotes(e.target.value); }}
                          placeholder="Add notes..."
                          className="input w-full h-20 resize-none"
                        />
                      </div>
                      <div className="flex gap-3 justify-end">
                        <button
                          onClick={() => rejectTimesheet(ts.id)}
                          disabled={processing === ts.id}
                          className="btn-sm btn-danger"
                        >
                          {processing === ts.id ? 'Processing...' : 'Reject'}
                        </button>
                        <button
                          onClick={() => approveTimesheet(ts.id)}
                          disabled={processing === ts.id}
                          className="btn-primary"
                        >
                          {processing === ts.id ? 'Processing...' : 'Approve'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
